
import React from 'react';

export const ServicesSection: React.FC = () => {
  const services = [
    {
      id: 1,
      title: 'iPhone Repair',
      description: 'Screen replacement, battery, charging port, and all iPhone models',
      icon: 'ri-smartphone-line',
      image: '/assets/iphone-repair-new.jpg',
      color: 'from-blue-500 to-blue-600',
      price: 'Starting ₹1,999'
    },
    {
      id: 2,
      title: 'Android Repair',
      description: 'Samsung, OnePlus, Xiaomi, and all Android device repairs',
      icon: 'ri-android-line',
      image: '/assets/android-repair-new.jpg',
      color: 'from-green-500 to-green-600',
      price: 'Starting ₹1,499'
    },
    {
      id: 3,
      title: 'iPad Repair',
      description: 'Screen, battery, charging port repairs for all iPad models',
      icon: 'ri-tablet-line',
      image: '/assets/ipad-repair-new.jpg',
      color: 'from-purple-500 to-purple-600',
      price: 'Starting ₹2,999'
    },
    {
      id: 4,
      title: 'Apple Watch',
      description: 'Screen, battery, crown, and strap repairs for Apple Watch',
      icon: 'ri-time-line',
      image: '/assets/applewatch-repair-new.jpg',
      color: 'from-red-500 to-red-600',
      price: 'Starting ₹2,499'
    },
    {
      id: 5,
      title: 'Smart Watch',
      description: 'All brands smartwatch repair and band replacement',
      icon: 'ri-watch-line',
      image: '/assets/smartwatch-repair-new.jpg',
      color: 'from-teal-500 to-teal-600',
      price: 'Starting ₹999'
    },
    {
      id: 6,
      title: 'Audio Devices',
      description: 'Headphones, earbuds, speakers repair and replacement',
      icon: 'ri-headphone-line',
      image: '/assets/audio-repair-new.jpg',
      color: 'from-orange-500 to-orange-600',
      price: 'Starting ₹799'
    }
  ];

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <i className="ri-tools-line text-red-600 text-2xl"></i>
            <span className="text-red-600 font-semibold text-lg">Our Services</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Expert Device Repair Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professional repair services for all your devices with genuine parts, 
            expert technicians, and transparent pricing. Same-day service available.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group hover:-translate-y-2 cursor-pointer"
            >
              {/* Image Section */}
              <div className="relative h-48 overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-10`}></div>
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${service.color} rounded-full flex items-center justify-center shadow-lg`}>
                    <i className={`${service.icon} text-white text-xl`}></i>
                  </div>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-900">
                    {service.price}
                  </span>
                </div>
              </div>

              {/* Content Section */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {service.description}
                </p>
                <div className="flex items-center justify-between">
                  <button className="text-red-600 hover:text-red-700 font-semibold flex items-center space-x-2 cursor-pointer">
                    <span>Learn More</span>
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </button>
                  <div className="flex items-center space-x-1 text-yellow-400">
                    <i className="ri-star-fill text-sm"></i>
                    <i className="ri-star-fill text-sm"></i>
                    <i className="ri-star-fill text-sm"></i>
                    <i className="ri-star-fill text-sm"></i>
                    <i className="ri-star-fill text-sm"></i>
                    <span className="text-gray-500 text-sm ml-1">4.9</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Features Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-check-line text-red-600 text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Genuine Parts</h3>
            <p className="text-gray-600">
              We use only authentic, high-quality parts for all repairs with warranty coverage.
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-time-line text-blue-600 text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Same Day Service</h3>
            <p className="text-gray-600">
              Most repairs completed within 2-4 hours. Express service available for urgent needs.
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-money-dollar-circle-line text-green-600 text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Best Price Guarantee</h3>
            <p className="text-gray-600">
              Competitive pricing with transparent quotes. No hidden charges or surprise fees.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-24 -translate-x-24"></div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center relative z-10">
            <div>
              <div className="inline-flex items-center bg-white/20 rounded-full px-4 py-2 mb-4">
                <i className="ri-fire-line text-yellow-300 mr-2"></i>
                <span className="text-sm font-semibold">Limited Time Offer</span>
              </div>
              <h3 className="text-4xl md:text-5xl font-bold mb-4">
                30% OFF
                <span className="block text-2xl md:text-3xl font-normal opacity-90">
                  All Smartphone Repairs
                </span>
              </h3>
              <p className="text-lg opacity-90 mb-6">
                Get your device fixed today and save big! Offer valid for all smartphone repairs this month.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
                  <i className="ri-phone-line mr-2"></i>
                  Book Now
                </button>
                <button className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
                  <i className="ri-calculator-line mr-2"></i>
                  Get Quote
                </button>
              </div>
            </div>
            <div className="relative flex justify-center">
              <img
                src="/assets/happy-customer-new.jpg"
                alt="Happy Customer"
                className="w-80 h-60 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -top-4 -right-4 bg-yellow-400 text-gray-900 px-4 py-2 rounded-full font-bold text-sm">
                <i className="ri-star-fill mr-1"></i>
                4.9/5 Rating
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};